import {
  type WorkOrderCreationPayload,
  type WorkOrderFormValues,
  type WorkOrderIssue,
} from 'types/Squares/WorkOrderManagement/work.order.creation.form.types';

type Status = 'open' | 'in progress' | 'completed';
interface CreationPayloadArgs {
  values: WorkOrderFormValues;
  customerId: string;
  userId: string;
  isWorkOrderReuest: boolean;
  currentDateWithTime: string;
  isUpdate: boolean;
  status: Status;
}

interface AssignmentPayloadArgs {
  values: WorkOrderFormValues;
  status: Status;
  isWorkOrderReuest: boolean;
  currentDateWithTime: string;
  isUpdate: boolean;
  userId: string;
}

interface CompletionPayloadArgs {
  values: WorkOrderFormValues;
  status: Status;
  isWorkOrderReuest: boolean;
  currentDateWithTime: string;
  isUpdate: boolean;
  userId: string;
}

/**
 * Generates the API payload for the work order creation step.
 * @param {CreationPayloadArgs} args - The arguments object.
 * @returns The formatted payload object for the creation API endpoint.
 */
export const getCreationPayload = ({
  values,
  customerId,
  userId,
  isWorkOrderReuest,
  currentDateWithTime,
  isUpdate,
  status,
}: CreationPayloadArgs): WorkOrderCreationPayload => {
  // Convert estimated time from days to hours for each component
  const formattedComponents = values.components.map((issue: WorkOrderIssue) => {
    return {
      ...issue,
      estimatedTime:
        issue.estimatedTimeUnit === 'Days'
          ? (Number(issue.estimatedTime) || 0) * 24
          : Number(issue.estimatedTime) || 0,
    };
  });

  return {
    assetId: values.assetId,
    customerId,
    source: values.source ?? null,
    approvedBy: isWorkOrderReuest
      ? null
      : isUpdate
      ? values?.approvedBy ?? (userId === '' ? null : userId)
      : userId === ''
      ? null
      : userId,
    approvedOn: isWorkOrderReuest
      ? null
      : isUpdate
      ? values?.approvedOn || currentDateWithTime
      : currentDateWithTime,
    isApproved: !isWorkOrderReuest,
    components: formattedComponents,
    creationDate: values.creationDate,
    priority: values.priority,
    maintenanceType: values.maintenanceType,
    assignedUserId: values?.assignedUserId,
    status,
  };
};

/**
 * Generates the API payload for the work order assignment step.
 * @param {AssignmentPayloadArgs} args - The arguments object.
 * @returns The formatted payload object for the assignment API endpoint.
 */
export const getAssignmentPayload = ({
  values,
  status,
  isWorkOrderReuest,
  isUpdate,
  currentDateWithTime,
  userId,
}: AssignmentPayloadArgs) => {
  const formattedComponents = values.components.map((issue: WorkOrderIssue) => {
    return {
      ...issue,
      estimatedTime:
        issue.estimatedTimeUnit === 'Days'
          ? (Number(issue.estimatedTime) || 0) * 24
          : Number(issue.estimatedTime) || 0,
    };
  });

  return {
    workInstruction: values.workInstruction,
    assignedUserId: values.assignedUserId,
    isApproved: !isWorkOrderReuest,
    approvedBy: isWorkOrderReuest
      ? null
      : isUpdate
      ? values?.approvedBy && values?.approvedBy !== ''
        ? values.approvedBy
        : userId !== ''
        ? userId
        : null
      : userId !== ''
      ? userId
      : null,
    approvedOn: isWorkOrderReuest
      ? null
      : isUpdate
      ? values?.approvedOn || currentDateWithTime
      : currentDateWithTime,
    status,
    components: formattedComponents,
  };
};

/**
 * Generates the API payload for the work order completion step.
 * @param {CompletionPayloadArgs} args - The arguments object.
 * @returns The formatted payload object for the completion API endpoint.
 */
export const getCompletionPayload = ({
  values,
  status,
  isWorkOrderReuest,
  currentDateWithTime,
  isUpdate,
  userId,
}: CompletionPayloadArgs) => {
  const formattedComponents = values.components.map((issue: WorkOrderIssue) => {
    // Calculate actual time in hours based on the individual component's time unit
    const actualTimeInHrs =
      issue.actualTimeUnit === 'Days'
        ? (Number(issue.actualTime) || 0) * 24
        : Number(issue.actualTime) || 0;

    return {
      ...issue,
      actualStartDate: issue.actualStartDate ?? '',
      actualEndDate: issue.actualEndDate ?? '',
      maintenanceActionPerformed: issue?.maintenanceActionPerformed ?? '',
      actualTime: actualTimeInHrs,
      partsCost: Number(issue.partsCost) ?? 0,
      laborCost: Number(issue.laborCost) ?? 0,
      totalCost: Number(issue.totalCost) ?? 0,
      currencyCode: issue.currencyCode ?? 'USD',
    };
  });

  return {
    isApproved: !isWorkOrderReuest,
    approvedBy:
      values?.approvedBy && values?.approvedBy !== ''
        ? values?.approvedBy
        : userId !== ''
        ? userId
        : null, // always send as its last step
    approvedOn: values?.approvedOn ?? currentDateWithTime,
    status,
    partsCost: Number(values?.partsCost) ?? 0,
    laborCost: Number(values?.laborCost) ?? 0,
    totalCost: Number(values?.totalCost) ?? 0,
    actualStartDate: values?.actualStartDate ?? '',
    actualEndDate: values?.actualEndDate ?? '',
    components: formattedComponents,
  };
};
